import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { WorldMap } from './worldMap';
import { getCountryFromCellId } from './worldMap/countryToCellMapping';
import { API_URL } from './config/apiConfig';
import CustomDropdown from './CustomDropdown';
import './WarEffortFortification.css';

const WAR_EFFORT_LAST_ATTACK_STRATEGY_KEY = 'warEffort_lastAttackStrategyId';

function getDefaultAttackStrategyId(userStrategies) {
  if (!userStrategies || userStrategies.length === 0) return null;
  const lastId = localStorage.getItem(WAR_EFFORT_LAST_ATTACK_STRATEGY_KEY);
  const lastStillAvailable = lastId && userStrategies.some(s => String(s.id) === String(lastId));
  return lastStillAvailable ? Number(lastId) : userStrategies[0].id;
}

const FORTIFICATION_TYPES = [
  { type: 'health_boost', name: 'Health Boost', cost: 280, description: 'All defending units have +1 health', unitId: 227 },
  { type: 'attack_boost', name: 'Attack Boost', cost: 320, description: 'All defending units have +1 attack', unitId: 77 },
  { type: 'intel_boost', name: 'Intel Boost', cost: 200, description: 'Defender has +1 intel', unitId: 237 },
  { type: 'order_boost', name: 'Order Boost', cost: 200, description: 'Defender has +1 order', unitId: 103 },
  { type: 'defend_twice', name: 'Defend Twice', cost: 420, description: 'Defender can defend twice', unitId: 187 },
  { type: 'instakill', name: 'Instakill', cost: 500, description: 'Defender can instakill a unit once', unitId: 60 },
  { type: 'deploy_random', name: 'Deploy Random', cost: 500, description: 'Defender can deploy a random unit costing max intel once', unitId: 279 },
  { type: 'ability_relay_drone', name: 'Ability Relay Drone', cost: 400, description: 'Defender starts with ability relay drone', unitId: 399 }
];

// Purpose to faction mapping (must match backend)
const PURPOSE_TO_FACTION_MAPPING = {
  'Consume': ['Asrah', 'The Hollow', 'The Forsaken Pact'],
  'Eradicate': ['Exolot Dominion', 'The Corrupted Core', 'The Iron Alliance'],
  'Colonize': ['League of Shadows', 'The Conspiracy', 'The Shadow Consortium'],
  'Protect': ['Ra\'s Children', 'The Divine Syndicate', 'The Covenant'],
  'Survive': ['Remnants of Humanity', 'The Last Crusade', 'The Silent Accord']
};

function WarEffortFortification({ mission, user, onBack, onFortificationComplete }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedFortification, setSelectedFortification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attackDialog, setAttackDialog] = useState({ show: false, countryCellId: null, fortificationType: null, countryName: null, selectedStrategyId: null });
  const [attackOtherCountryDialog, setAttackOtherCountryDialog] = useState({ show: false, countryCellId: null, countryName: null, ownerFaction: null, selectedStrategyId: null });
  // Format initial mission data
  const formatMissionData = (missionData) => {
    if (!missionData) return null;
    const formatted = { ...missionData };
    // Handle PostgreSQL array format for fortified countries
    if (formatted.player1_fortified_countries && !Array.isArray(formatted.player1_fortified_countries)) {
      if (typeof formatted.player1_fortified_countries === 'string') {
        const cleaned = formatted.player1_fortified_countries.replace(/[{}]/g, '');
        formatted.player1_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
      } else {
        formatted.player1_fortified_countries = [];
      }
    }
    if (formatted.player2_fortified_countries && !Array.isArray(formatted.player2_fortified_countries)) {
      if (typeof formatted.player2_fortified_countries === 'string') {
        const cleaned = formatted.player2_fortified_countries.replace(/[{}]/g, '');
        formatted.player2_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
      } else {
        formatted.player2_fortified_countries = [];
      }
    }
    return formatted;
  };
  
  const [missionData, setMissionData] = useState(() => formatMissionData(mission));
  const [controlledCountries, setControlledCountries] = useState([]);
  const [warEffortData, setWarEffortData] = useState(null);
  const [playerResource, setPlayerResource] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [opponentUsername, setOpponentUsername] = useState(null);
  const [userStrategies, setUserStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  
  // Get user ID from JWT token (more reliable than localStorage, especially for private windows)
  const userId = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.userId ? String(decoded.userId) : null;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    // Fallback to localStorage if token decode fails
    return localStorage.getItem('userId');
  }, []);
  
  // Verify that the current user is actually part of this mission
  const isUserInMission = useMemo(() => {
    if (!userId || !missionData) return false;
    const userIdNum = Number(userId);
    const player1Id = Number(missionData.player1_id);
    const player2Id = missionData.player2_id ? Number(missionData.player2_id) : null;
    return userIdNum === player1Id || userIdNum === player2Id;
  }, [userId, missionData?.player1_id, missionData?.player2_id]);
  
  // Compare as numbers to handle type mismatches - memoize to prevent unnecessary recalculations
  const isPlayer1 = useMemo(() => {
    return userId ? Number(missionData?.player1_id) === Number(userId) : false;
  }, [userId, missionData?.player1_id]);
  
  // Debug logging - DISABLED to prevent infinite re-renders
  // Arrays/objects in dependencies cause infinite loops
  // Uncomment only for debugging specific issues
  /*
  useEffect(() => {
    console.log('🔍 User ID detection:', {
      user,
      userId,
      userType: typeof user,
      localStorageUserId: localStorage.getItem('userId'),
      missionPlayer1Id: missionData?.player1_id,
      missionPlayer2Id: missionData?.player2_id,
      isPlayer1,
      player1_fortified: missionData?.player1_fortified_countries,
      player2_fortified: missionData?.player2_fortified_countries
    });
  }, [userId, missionData?.player1_id, missionData?.player2_id, isPlayer1]);
  */
  
  // Get fortified countries array, ensuring it's an array and converting to numbers
  // Handle PostgreSQL array format (string like "{251,252,253}") or array
  const getFortifiedCountriesArray = (countries) => {
    if (!countries) return [];
    if (Array.isArray(countries)) {
      return countries.map(id => Number(id)).filter(id => !isNaN(id));
    }
    // Handle PostgreSQL array format (string like "{251,252,253}")
    if (typeof countries === 'string') {
      const cleaned = countries.replace(/[{}]/g, '');
      if (cleaned.trim() === '') return [];
      return cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id));
    }
    return [];
  };
  
  // Memoize to prevent recalculation on every render
  // Player's own fortified countries (for display and validation)
  const playerFortifiedCountries = useMemo(() => {
    return getFortifiedCountriesArray(
      isPlayer1 ? missionData?.player1_fortified_countries : missionData?.player2_fortified_countries
    );
  }, [isPlayer1, missionData?.player1_fortified_countries, missionData?.player2_fortified_countries]);
  
  // Opponent's fortified countries (for display so player knows what to attack)
  const opponentFortifiedCountries = useMemo(() => {
    return getFortifiedCountriesArray(
      isPlayer1 ? missionData?.player2_fortified_countries : missionData?.player1_fortified_countries
    );
  }, [isPlayer1, missionData?.player1_fortified_countries, missionData?.player2_fortified_countries]);
  
  // Debug logging - DISABLED to prevent infinite re-renders
  // Arrays/objects in dependencies cause infinite loops
  // Uncomment only for debugging specific issues
  /*
  useEffect(() => {
    console.log('🔍 Fortified countries debug:', {
      isPlayer1,
      player1_id: missionData?.player1_id,
      player2_id: missionData?.player2_id,
      user_id: userId,
      playerFortifiedCountries: playerFortifiedCountries.length,
      missionDataExists: !!missionData
    });
  }, [userId, missionData?.player1_id, missionData?.player2_id, isPlayer1]);
  */
  
  // Get headquarters image path for a faction
  const getHeadquartersImage = (factionName) => {
    const imageMap = {
      'Remnants of Humanity': '/images/remnants_headquarter.jpg',
      'Exolot Dominion': '/images/exolot_landing.jpg',
      'League of Shadows': '/images/league_portal.jpg',
      "Ra's Children": '/images/ra_seat.jpg',
      'Asrah': '/images/asrah_ground.jpg'
    };
    return imageMap[factionName] || null;
  };
  
  // Get cell data for tooltip
  const getCellData = (cellId) => {
    if (!warEffortData?.cells) return null;
    return warEffortData.cells.find(c => c.cell_id === cellId);
  };
  
  // Check if a cell is a headquarters
  const isHeadquarters = (cellId) => {
    const cell = getCellData(cellId);
    return cell?.is_headquarters === true || cell?.is_headquarters === 'true' || cell?.is_headquarters === 't';
  };
  
  // Handle country hover for tooltip
  const handleCountryHover = useCallback((cellId, e) => {
    if (!cellId) return;
    setHoveredCell(cellId);
    
    // Calculate tooltip position
    const tooltipWidth = 250;
    const padding = 10;
    let x = e.clientX + padding;
    let y = e.clientY;
    
    // Adjust if tooltip would go off-screen
    if (x + tooltipWidth > window.innerWidth) {
      x = e.clientX - tooltipWidth - padding;
    }
    if (y + 200 > window.innerHeight) {
      y = window.innerHeight - 200;
    }
    if (y < 0) {
      y = 10;
    }
    
    setTooltipPosition({ x, y });
  }, []);
  
  // Handle country mouse leave
  const handleCountryLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);
  
  // Initialize missionData from prop on mount, then refresh
  useEffect(() => {
    // First, ensure the initial mission prop data is properly formatted
    if (mission) {
      const formattedMission = { ...mission };
      // Handle PostgreSQL array format for initial data
      if (formattedMission.player1_fortified_countries && !Array.isArray(formattedMission.player1_fortified_countries)) {
        if (typeof formattedMission.player1_fortified_countries === 'string') {
          const cleaned = formattedMission.player1_fortified_countries.replace(/[{}]/g, '');
          formattedMission.player1_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          formattedMission.player1_fortified_countries = [];
        }
      }
      if (formattedMission.player2_fortified_countries && !Array.isArray(formattedMission.player2_fortified_countries)) {
        if (typeof formattedMission.player2_fortified_countries === 'string') {
          const cleaned = formattedMission.player2_fortified_countries.replace(/[{}]/g, '');
          formattedMission.player2_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          formattedMission.player2_fortified_countries = [];
        }
      }
      setMissionData(formattedMission);
    }
  }, [mission]);
  
  // Refresh mission data on mount to ensure we have the latest data
  useEffect(() => {
    const fetchLatestMission = async () => {
      try {
        const token = localStorage.getItem('token');
        // Fetch the full mission data, not just status
        const response = await fetch(`${API_URL}/war-effort/mission/${mission.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const missionData = await response.json();
          // Reduced logging - only log in dev mode
          if (process.env.NODE_ENV === 'development') {
            console.log('Fetched mission data:', {
              missionId: missionData.id,
              hasPlayer1Fortified: !!missionData.player1_fortified_countries,
              hasPlayer2Fortified: !!missionData.player2_fortified_countries
            });
          }
          
          // Ensure fortified countries arrays are properly formatted
          // Handle PostgreSQL array format (string like "{251,252,253}") or array
          if (missionData.player1_fortified_countries && !Array.isArray(missionData.player1_fortified_countries)) {
            if (typeof missionData.player1_fortified_countries === 'string') {
              const cleaned = missionData.player1_fortified_countries.replace(/[{}]/g, '');
              missionData.player1_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
            } else {
              missionData.player1_fortified_countries = [];
            }
          }
          if (missionData.player2_fortified_countries && !Array.isArray(missionData.player2_fortified_countries)) {
            if (typeof missionData.player2_fortified_countries === 'string') {
              const cleaned = missionData.player2_fortified_countries.replace(/[{}]/g, '');
              missionData.player2_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
            } else {
              missionData.player2_fortified_countries = [];
            }
          }
          
          // Removed excessive logging
          
          setMissionData(missionData);
        }
      } catch (error) {
        console.error('Error fetching latest mission data:', error);
      }
    };
    
    fetchLatestMission();
  }, [mission.id]);
  
  // Faction colors for map display
  const factionColors = {
    'Remnants of Humanity': '#556B2F',
    'Exolot Dominion': '#4B0082',
    'League of Shadows': '#1A1A1A',
    'Ra\'s Children': '#F4A460',
    'Asrah': '#8B0000',
    'The Sentients': '#ADD8E6',
    'Combined': '#FFA500'
  };
  
  const getFactionBackground = (faction) => {
    if (!faction) return '#808080';
    const color = factionColors[faction];
    if (!color) return '#808080';
    
    // Convert hex to rgba
    const hexToRgba = (hex, alpha = 1.0) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    if (color.includes('gradient')) {
      return color.replace(/#([0-9A-Fa-f]{6})/g, (match, hex) => {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 1.0)`;
      });
    } else {
      return hexToRgba(color, 1.0);
    }
  };
  
  // Create cell owners map from war effort data
  const cellOwnersMap = useMemo(() => {
    if (!warEffortData?.cells) return {};
    const map = {};
    warEffortData.cells.forEach(cell => {
      if (cell.owner_name) {
        map[String(cell.cell_id)] = cell.owner_name;
      }
    });
    return map;
  }, [warEffortData]);
  
  // Fetch controlled countries (faction-based) and war effort data
  // Use ref to track if we're already fetching to prevent infinite loops
  const fetchingRef = useRef(false);
  
  const fetchControlledCountries = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (fetchingRef.current) {
      return;
    }
    
    // Ensure missionData has purpose before proceeding
    if (!missionData || (!missionData.player1_purpose && !missionData.player2_purpose)) {
      return;
    }
    
    fetchingRef.current = true;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWarEffortData(data);
        
        // Get player's purpose from mission data
        let playerPurpose = isPlayer1 ? missionData.player1_purpose : missionData.player2_purpose;
        
        // If purpose is missing, try to get it from participant data
        if (!playerPurpose && data.participant && data.participant.purpose) {
          playerPurpose = data.participant.purpose;
        }
        
        if (!playerPurpose) {
          console.warn('⚠️ No purpose found for player');
          setError('Your purpose is not set. Please ensure you have joined the mission with a purpose.');
          fetchingRef.current = false;
          return;
        }
        
        // Get allowed factions for this purpose
        const allowedFactions = PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [];
        
        // Filter cells controlled by any of the player's allowed factions
        // A country belongs to the player if its owner_name matches any faction in their purpose's allowed factions
        const controlled = data.cells
          .filter(cell => {
            // Check if the owner_name matches any of the allowed factions
            const matches = cell.owner_name && allowedFactions.includes(cell.owner_name);
            return matches;
          })
          .map(cell => Number(cell.cell_id)); // Ensure cell_id is a number
        
        // Only log warnings for important issues
        if (controlled.length === 0) {
          console.warn('⚠️ No controlled countries found! This means you cannot fortify any countries.');
        }
        
        setControlledCountries(controlled);
      }
      
      // Also fetch mission status to get resource info
      const missionStatusResponse = await fetch(`${API_URL}/war-effort/mission-status/${missionData.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (missionStatusResponse.ok) {
        const statusData = await missionStatusResponse.json();
        const resource = statusData.isPlayer1 ? statusData.player1Resource : statusData.player2Resource;
        setPlayerResource(resource);
      }
    } catch (error) {
      console.error('Error fetching controlled countries:', error);
    } finally {
      fetchingRef.current = false;
    }
  }, [missionData?.id, missionData?.player1_purpose, missionData?.player2_purpose, isPlayer1]);
  
  // Only fetch when mission ID or player status changes, not on every render
  useEffect(() => {
    if (missionData?.id && (missionData?.player1_purpose || missionData?.player2_purpose)) {
      fetchControlledCountries();
    }
  }, [missionData?.id, missionData?.player1_purpose, missionData?.player2_purpose, isPlayer1]);
  
  // Fetch opponent username
  useEffect(() => {
    const fetchOpponentUsername = async () => {
      if (!missionData || !userId) {
        setOpponentUsername(null);
        return;
      }
      
      // Don't show opponent if mission is waiting for opponent
      if (missionData.status === 'waiting_opponent') {
        setOpponentUsername(null);
        return;
      }
      
      // Get opponent ID
      const opponentId = isPlayer1 ? missionData.player2_id : missionData.player1_id;
      
      if (!opponentId) {
        setOpponentUsername(null);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/player-ranks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userIds: [opponentId] })
        });
        
        if (response.ok) {
          const ranks = await response.json();
          const opponentData = ranks.find(r => String(r.id) === String(opponentId));
          if (opponentData && opponentData.username) {
            setOpponentUsername(opponentData.username);
          } else {
            setOpponentUsername('Opponent');
          }
        } else {
          setOpponentUsername('Opponent');
        }
      } catch (error) {
        console.error('Error fetching opponent username:', error);
        setOpponentUsername('Opponent');
      }
    };
    
    fetchOpponentUsername();
  }, [missionData?.player1_id, missionData?.player2_id, missionData?.status, isPlayer1, userId]);
  
  // Fetch user's strategies for their purpose
  useEffect(() => {
    // Safety check: ensure component is mounted and state setters are available
    if (typeof setUserStrategies !== 'function' || typeof setSelectedStrategyId !== 'function') {
      console.error('State setters not available');
      return;
    }
    
    const fetchUserStrategies = async () => {
      if (!missionData || !userId) return;
      
      const playerPurpose = isPlayer1 ? missionData.player1_purpose : missionData.player2_purpose;
      if (!playerPurpose) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const currentLanguage = window.i18n?.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/war-effort/strategies?lang=${encodeURIComponent(normalizedLanguage)}&purpose=${encodeURIComponent(playerPurpose)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const strategies = await response.json();
          if (Array.isArray(strategies)) {
            setUserStrategies(strategies);
            // Auto-select first strategy if available (use functional update to avoid stale closure)
            if (strategies.length > 0) {
              setSelectedStrategyId(prev => {
                // Only set if not already set
                if (prev) return prev;
                return strategies[0].id;
              });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user strategies:', error);
      }
    };
    
    fetchUserStrategies();
  }, [missionData?.id, missionData?.player1_purpose, missionData?.player2_purpose, isPlayer1, userId]);
  
  const handleCountryClick = (cellId) => {
    const cellIdNum = Number(cellId);
    
    // FIRST: Check if this is an opponent's fortified country (for attacking)
    if (opponentFortifiedCountries.includes(cellIdNum) || opponentFortifiedCountries.includes(cellId)) {
      // Get fortification type from mission data
      const opponentFortifications = isPlayer1 ? missionData?.player2_fortifications : missionData?.player1_fortifications;
      // Try both string and number keys (JSONB keys might be strings)
      const fortificationData = opponentFortifications && (
        opponentFortifications[cellIdNum] || 
        opponentFortifications[String(cellIdNum)] ||
        opponentFortifications[cellId] ||
        opponentFortifications[String(cellId)]
      ) ? (opponentFortifications[cellIdNum] || opponentFortifications[String(cellIdNum)] || opponentFortifications[cellId] || opponentFortifications[String(cellId)]) : null;
      const fortificationType = fortificationData?.type || 'unknown';
      
      // Find fortification name from FORTIFICATION_TYPES
      const fortification = FORTIFICATION_TYPES.find(f => f.type === fortificationType);
      const fortificationName = fortification ? fortification.name : fortificationType;
      
      // Show attack dialog with strategy selection; preselect last used strategy if still available
      const defaultStrategyId = getDefaultAttackStrategyId(userStrategies);
      setAttackDialog({
        show: true,
        countryCellId: cellIdNum,
        fortificationType: fortificationName,
        countryName: getCountryFromCellId(cellIdNum),
        selectedStrategyId: defaultStrategyId
      });
      setError(null);
      return;
    }
    
    const isControlled = controlledCountries.includes(cellIdNum) || controlledCountries.includes(cellId);
    
    // Check if country is owned by another faction (not player's, not opponent's)
    const cellOwner = cellOwnersMap[String(cellIdNum)] || cellOwnersMap[String(cellId)];
    if (cellOwner && !isControlled) {
      // Get player's purpose and allowed factions
      const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
      const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
      
      // Get opponent's purpose and allowed factions
      const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
      const opponentAllowedFactions = opponentPurpose ? PURPOSE_TO_FACTION_MAPPING[opponentPurpose] || [] : [];
      
      // Check if the owner is NOT in player's or opponent's allowed factions
      const isPlayerFaction = allowedFactions.includes(cellOwner);
      const isOpponentFaction = opponentAllowedFactions.includes(cellOwner);
      
      if (!isPlayerFaction && !isOpponentFaction) {
        // Check if second player has joined before allowing attack
        if (!missionData?.player2_id) {
          setError('Cannot attack other countries until a second player has joined the mission');
          return;
        }
        
        // This country is owned by another faction - can attack it; preselect last used strategy if still available
        const defaultStrategyId = getDefaultAttackStrategyId(userStrategies);
        setAttackOtherCountryDialog({
          show: true,
          countryCellId: cellIdNum,
          countryName: getCountryFromCellId(cellIdNum),
          ownerFaction: cellOwner,
          selectedStrategyId: defaultStrategyId
        });
        setError(null);
        return;
      }
      if (isOpponentFaction) {
        // Opponent's country that is not fortified: must destroy all fortifications before attacking other countries
        setError('Destroy all fortifications before attacking other countries');
        return;
      }
    }
    
    // Debug: Log when click fails
    if (!isControlled && controlledCountries.length > 0) {
      console.warn('Country not controlled:', {
        cellId,
        cellIdNum,
        isInArray: controlledCountries.includes(cellIdNum),
        controlledCount: controlledCountries.length,
        sampleControlled: controlledCountries.slice(0, 5)
      });
    }
    
    // Check if country is already fortified
    if (playerFortifiedCountries.includes(cellId) || playerFortifiedCountries.includes(cellIdNum)) {
      setError('This country is already fortified');
      return;
    }
    
    // Check if max fortified countries reached
    if (playerFortifiedCountries.length >= 3) {
      setError('You have already fortified the maximum of 3 countries');
      return;
    }
    
    // Check if country is controlled by player's faction (based on purpose)
    if (!isControlled) {
      setError(`You do not control this country. You can only fortify countries controlled by your faction. (Cell ID: ${cellId}, Controlled: [${controlledCountries.join(', ')}])`);
      return;
    }
    
    setSelectedCountry(cellId);
    setSelectedFortification(null);
    setError(null);
  };
  
  const handleFortifyWithType = async (fortificationType) => {
    if (!selectedCountry || !fortificationType) {
      setError('Please select a country and fortification type');
      return;
    }

    const fortification = FORTIFICATION_TYPES.find(f => f.type === fortificationType);
    if (!fortification) {
      setError('Invalid fortification type');
      return;
    }

    if (playerResources < fortification.cost) {
      setError(`Insufficient resources. Need ${fortification.cost}, have ${playerResources}`);
      return;
    }

    if (playerFortifiedCountries.length >= 3) {
      setError('You have already fortified the maximum of 3 countries');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSelectedFortification(fortificationType);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/fortify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: selectedCountry,
          fortificationType: fortificationType
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Update mission data
        const updatedMission = { ...missionData };
        if (isPlayer1) {
          updatedMission.player1_resources = result.remainingResources;
          updatedMission.player1_fortified_countries = [...playerFortifiedCountries, selectedCountry];
        } else {
          updatedMission.player2_resources = result.remainingResources;
          updatedMission.player2_fortified_countries = [...playerFortifiedCountries, selectedCountry];
        }
        setMissionData(updatedMission);

        // Reset selection
        setSelectedCountry(null);
        setSelectedFortification(null);

        // Refresh controlled countries
        fetchControlledCountries();

        if (onFortificationComplete) {
          onFortificationComplete(updatedMission);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fortify country');
        setSelectedFortification(null);
      }
    } catch (error) {
      console.error('Error fortifying country:', error);
      setError('Failed to fortify country');
      setSelectedFortification(null);
    } finally {
      setLoading(false);
    }
  };
  
  const playerResources = playerResource ? playerResource.amount : 0;
  const availableFortifications = FORTIFICATION_TYPES.filter(f => playerResources >= f.cost);
  
  // Handle attack on opponent's fortified country
  const handleAttackOpponentFortified = async () => {
    if (!attackDialog.countryCellId) {
      setError('Missing country information to launch attack');
      setAttackDialog({ show: false, countryCellId: null, fortificationType: null, countryName: null, selectedStrategyId: null });
      return;
    }
    
    // Use strategy selected in dialog
    const strategyIdToUse = attackDialog.selectedStrategyId;
    
    if (!strategyIdToUse) {
      setError('Please select a strategy to launch attack');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/attack-fortified`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: attackDialog.countryCellId,
          strategyId: strategyIdToUse
        })
      });
      
      if (response.ok) {
        localStorage.setItem(WAR_EFFORT_LAST_ATTACK_STRATEGY_KEY, String(strategyIdToUse));
        const { gameId, gameState } = await response.json();
        setAttackDialog({ show: false, countryCellId: null, fortificationType: null, countryName: null, selectedStrategyId: null });
        navigate(`/battle/${gameId}`, { state: { gameState, gameId } });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to attack fortified country');
        // Don't close dialog on error - let user try again
      }
    } catch (error) {
      console.error('Error attacking fortified country:', error);
      setError('Failed to attack fortified country');
      // Don't close dialog on error - let user try again
    } finally {
      setLoading(false);
    }
  };
  
  // Handle attack on other faction's country
  const handleAttackOtherCountry = async () => {
    if (!attackOtherCountryDialog.countryCellId) {
      setError('Missing country information to launch attack');
      setAttackOtherCountryDialog({ show: false, countryCellId: null, countryName: null, ownerFaction: null, selectedStrategyId: null });
      return;
    }
    
    // Use strategy selected in dialog
    const strategyIdToUse = attackOtherCountryDialog.selectedStrategyId;
    
    if (!strategyIdToUse) {
      setError('Please select a strategy to launch attack');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/attack-other-country`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: attackOtherCountryDialog.countryCellId,
          strategyId: strategyIdToUse
        })
      });
      
      if (response.ok) {
        localStorage.setItem(WAR_EFFORT_LAST_ATTACK_STRATEGY_KEY, String(strategyIdToUse));
        const { gameId, gameState } = await response.json();
        setAttackOtherCountryDialog({ show: false, countryCellId: null, countryName: null, ownerFaction: null, selectedStrategyId: null });
        navigate(`/battle/${gameId}`, { state: { gameState, gameId } });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to attack country');
        // Don't close dialog on error - let user try again
      }
    } catch (error) {
      console.error('Error attacking other country:', error);
      setError('Failed to attack country');
      // Don't close dialog on error - let user try again
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="war-effort-fortification" style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {/* Resource display - upper middle of screen */}
      {playerResource && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2000,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          padding: '12px 24px',
          borderRadius: '8px',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)',
          pointerEvents: 'none',
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold',
          textAlign: 'center',
          minWidth: '200px'
        }}>
          {playerResource.amount} {playerResource.displayName}
        </div>
      )}
      
      {/* World map - full screen, no borders or background content */}
      <div className="world-map-container" style={{ border: 'none', margin: 0, padding: 0, position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1 }}>
        <WorldMap
          cellOwnersMap={cellOwnersMap}
          getFactionBackground={getFactionBackground}
          onCountryClick={handleCountryClick}
          onCountryHover={handleCountryHover}
          onCountryLeave={handleCountryLeave}
          highlightedCells={controlledCountries}
          fortifiedCells={playerFortifiedCountries}
          opponentFortifiedCells={opponentFortifiedCountries}
          selectedCell={selectedCountry}
        />
        
        {/* Mission and Opponent Info Overlay */}
        {(() => {
          // First, verify that the current user is part of this mission
          if (!isUserInMission) {
            // User is not part of this mission - don't show anything
            return null;
          }
          
          // Only show mission info when opponent exists (mission is active)
          const hasOpponent = missionData?.status === 'active' && 
            (isPlayer1 ? missionData?.player2_id : missionData?.player1_id);
          
          // Don't show mission until opponent exists - mission target depends on opponent's headquarters
          if (!hasOpponent) {
            // Only show purpose while waiting for opponent
            const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
            return playerPurpose && (
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '15px 20px',
                zIndex: 1000,
                color: 'white',
                fontSize: '16px',
                fontFamily: 'Arial, sans-serif',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)',
                minWidth: '300px'
              }}>
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', color: '#4ECDC4' }}>Your Purpose: </span>
                  <span>{playerPurpose}</span>
                </div>
                <div style={{ marginTop: '10px', fontSize: '14px', color: '#CCCCCC', fontStyle: 'italic' }}>
                  Waiting for opponent to join...
                </div>
              </div>
            );
          }
          
          // Calculate the correct mission name for this player (only when opponent exists)
          const PURPOSE_TO_MISSION_TYPE = {
            'Consume': 'Infect key officials',
            'Eradicate': 'Sabotage production',
            'Colonize': 'Infiltrate main communities',
            'Protect': 'Weaken enemy forces',
            'Survive': 'Raid targets'
          };
          
          const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
          // Use player-specific target country if available, otherwise fall back to shared mission_target_country
          const playerTargetCountry = isPlayer1 
            ? (missionData?.player1_mission_target_country || missionData?.mission_target_country)
            : (missionData?.player2_mission_target_country || missionData?.mission_target_country);
          const playerMissionType = playerPurpose ? PURPOSE_TO_MISSION_TYPE[playerPurpose] : null;
          const playerMissionName = (playerMissionType && playerTargetCountry) 
            ? `${playerMissionType} in ${playerTargetCountry}` 
            : missionData?.mission_name; // Fallback to stored mission name
          
          // Get opponent's mission name with their own target country
          const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
          const opponentTargetCountry = isPlayer1
            ? (missionData?.player2_mission_target_country || missionData?.mission_target_country)
            : (missionData?.player1_mission_target_country || missionData?.mission_target_country);
          const opponentMissionType = opponentPurpose ? PURPOSE_TO_MISSION_TYPE[opponentPurpose] : null;
          const opponentMissionName = (opponentMissionType && opponentTargetCountry)
            ? `${opponentMissionType} in ${opponentTargetCountry}`
            : null;
          
          return (playerMissionName || opponentUsername || playerPurpose) && (
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              padding: '15px 20px',
              zIndex: 1000,
              color: 'white',
              fontSize: '16px',
              fontFamily: 'Arial, sans-serif',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)',
              minWidth: '300px'
            }}>
              {playerPurpose && (
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', color: '#4ECDC4' }}>Your Purpose: </span>
                  <span>{playerPurpose}</span>
                </div>
              )}
              {playerMissionName && (
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', color: '#FFD700' }}>Your Mission: </span>
                  <span>{playerMissionName}</span>
                </div>
              )}
              {opponentUsername && (
                <div>
                  <span style={{ fontWeight: 'bold', color: '#FF6B6B' }}>Your Opponent: </span>
                  <span>{opponentUsername}</span>
                  {opponentMissionName && (
                    <span style={{ fontSize: '14px', color: '#CCCCCC', marginLeft: '8px' }}>
                      ({opponentMissionName})
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>
      
      {/* Country Tooltip */}
      {hoveredCell !== null && (
        <div
          className="country-tooltip"
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            padding: '15px',
            zIndex: 2000,
            minWidth: '200px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)',
            pointerEvents: 'none'
          }}
        >
          <div style={{ 
            color: 'white', 
            fontSize: '14px', 
            fontWeight: 'bold', 
            marginBottom: '10px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            paddingBottom: '8px'
          }}>
            {getCountryFromCellId(hoveredCell)}
          </div>
          {(() => {
            const cell = getCellData(hoveredCell);
            if (!cell) return <div style={{ color: 'white' }}>No data</div>;
            
            // For headquarters, show image and owner
            if (isHeadquarters(hoveredCell) && cell.owner_name) {
              const headquartersImage = getHeadquartersImage(cell.owner_name);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {headquartersImage && (
                    <img
                      src={headquartersImage}
                      alt={cell.owner_name}
                      style={{
                        width: '250px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        marginBottom: '6px'
                      }}
                    />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: getFactionBackground(cell.owner_name),
                        flexShrink: 0
                      }}
                    />
                    <span style={{ color: 'white', fontSize: '13px', flex: 1 }}>
                      {cell.owner_name}
                    </span>
                    <span style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                      Headquarters
                    </span>
                  </div>
                </div>
              );
            }
            
            // For regular countries, show owner and fortification if applicable
            const cellIdNum = Number(hoveredCell);
            const isPlayerFortified = playerFortifiedCountries.includes(cellIdNum) || playerFortifiedCountries.includes(hoveredCell);
            const isOpponentFortified = opponentFortifiedCountries.includes(cellIdNum) || opponentFortifiedCountries.includes(hoveredCell);
            
            // Get fortification data (same pattern as attack dialog)
            let fortificationData = null;
            let fortification = null;
            
            if (isPlayerFortified || isOpponentFortified) {
              const fortifications = isPlayerFortified 
                ? (isPlayer1 ? missionData?.player1_fortifications : missionData?.player2_fortifications)
                : (isPlayer1 ? missionData?.player2_fortifications : missionData?.player1_fortifications);
              
              if (fortifications) {
                // Try both string and number keys (JSONB keys might be strings) - same pattern as attack dialog
                fortificationData = (
                  fortifications[cellIdNum] || 
                  fortifications[String(cellIdNum)] ||
                  fortifications[hoveredCell] || 
                  fortifications[String(hoveredCell)]
                ) ? (fortifications[cellIdNum] || fortifications[String(cellIdNum)] || fortifications[hoveredCell] || fortifications[String(hoveredCell)]) : null;
                
                if (fortificationData) {
                  const fortificationType = fortificationData.type || 'unknown';
                  fortification = FORTIFICATION_TYPES.find(f => f.type === fortificationType);
                }
              }
            }
            
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Owner info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {cell.owner_name ? (
                    <>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: getFactionBackground(cell.owner_name),
                          flexShrink: 0
                        }}
                      />
                      <span style={{ color: 'white', fontSize: '13px', flex: 1 }}>
                        {cell.owner_name}
                      </span>
                    </>
                  ) : (
                    <span style={{ color: '#aaa', fontSize: '13px' }}>Unclaimed</span>
                  )}
                </div>
                
                {/* Fortification info */}
                {fortification && (
                  <div style={{ 
                    marginTop: '8px', 
                    paddingTop: '8px', 
                    borderTop: '1px solid rgba(255, 255, 255, 0.2)' 
                  }}>
                    <div style={{ 
                      color: '#4ECDC4', 
                      fontSize: '12px', 
                      fontWeight: 'bold', 
                      marginBottom: '6px' 
                    }}>
                      Fortification
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {fortification.unitId && (
                        <img
                          src={`/images/${fortification.unitId}.jpg`}
                          alt={fortification.name}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid rgba(255, 255, 255, 0.3)'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                          {fortification.name}
                        </div>
                        <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                          {fortification.description}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
      
      {/* Fortification selection - brought to front with high z-index */}
      {selectedCountry && (
        <div className="fortification-selection" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 3000, backgroundColor: 'rgba(0, 0, 0, 0.95)', padding: '30px', borderRadius: '12px', border: '2px solid rgba(255, 255, 255, 0.3)', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', pointerEvents: 'auto' }}>
          <h3 style={{ color: 'white', marginBottom: '20px' }}>Select Fortification for {getCountryFromCellId(selectedCountry)}</h3>
          
          {availableFortifications.length === 0 ? (
            <div className="error" style={{ color: '#ff4444', textAlign: 'center' }}>Insufficient resources for any fortification</div>
          ) : (
            <div className="fortification-options">
              {FORTIFICATION_TYPES.map(fort => (
                <div
                  key={fort.type}
                  className={`fortification-option ${selectedFortification === fort.type ? 'selected' : ''} ${playerResources < fort.cost ? 'disabled' : ''} ${loading ? 'loading' : ''}`}
                  onClick={() => {
                    if (playerResources >= fort.cost && !loading && playerFortifiedCountries.length < 3) {
                      setSelectedFortification(fort.type);
                      // Fortify immediately
                      handleFortifyWithType(fort.type);
                    }
                  }}
                  style={{ 
                    cursor: (playerResources >= fort.cost && !loading && playerFortifiedCountries.length < 3) ? 'pointer' : 'not-allowed', 
                    pointerEvents: 'auto',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '200px'
                  }}
                >
                  {fort.unitId && (
                    <img 
                      src={`/images/${fort.unitId}.jpg`} 
                      alt={fort.name}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 0
                      }}
                      onError={(e) => {
                        // Fallback if image doesn't exist
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div style={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    padding: '15px',
                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%)'
                  }}>
                    <div className="fortification-name" style={{ 
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                      marginBottom: '5px'
                    }}>{fort.name}</div>
                    <div className="fortification-cost" style={{ 
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                      marginBottom: '5px'
                    }}>Cost: {fort.cost}</div>
                    <div className="fortification-description" style={{ 
                      textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)',
                      fontSize: '0.85em'
                    }}>{fort.description}</div>
                    {loading && selectedFortification === fort.type && (
                      <div style={{ 
                        color: '#4a9eff', 
                        marginTop: '10px', 
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)'
                      }}>Fortifying...</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <button
            onClick={() => {
              setSelectedCountry(null);
              setSelectedFortification(null);
              setError(null);
            }}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: '#555',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
              pointerEvents: 'auto'
            }}
          >
            ✕ Close
          </button>
        </div>
      )}
      
      {error && (
        <div className="error-message" style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 3001, backgroundColor: 'rgba(255, 68, 68, 0.95)', color: 'white', padding: '15px 30px', borderRadius: '8px', border: '2px solid rgba(255, 255, 255, 0.3)', pointerEvents: 'auto' }}>
          {error}
        </div>
      )}
      
      {/* Attack Dialog */}
      {attackDialog.show && (
        <div className="attack-dialog-overlay" style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          backgroundColor: 'rgba(0, 0, 0, 0.8)', 
          zIndex: 4000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div className="attack-dialog" style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.95)', 
            border: '2px solid rgba(255, 255, 255, 0.3)', 
            borderRadius: '12px', 
            padding: '30px', 
            maxWidth: '500px', 
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#FFD700' }}>
              Attack Fortified Country
            </h3>
            <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.6' }}>
              This country ({attackDialog.countryName}) has been fortified with <strong style={{ color: '#4ECDC4' }}>{attackDialog.fortificationType}</strong>.
            </p>
            
            {/* Strategy Selection */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', color: '#CCCCCC' }}>
                Select Strategy:
              </label>
              {userStrategies.length === 0 ? (
                <div style={{ color: '#ff4444', fontSize: '14px', marginBottom: '10px' }}>
                  No strategies available for this purpose
                </div>
              ) : (
                <CustomDropdown
                  options={userStrategies.map(s => ({ value: s.id, label: s.name }))}
                  value={attackDialog.selectedStrategyId}
                  onChange={(value) => setAttackDialog(prev => ({ ...prev, selectedStrategyId: value }))}
                  placeholder="Select a strategy"
                />
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAttackDialog({ show: false, countryCellId: null, fortificationType: null, countryName: null, selectedStrategyId: null })}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                NO
              </button>
              <button
                onClick={handleAttackOpponentFortified}
                disabled={loading || !attackDialog.selectedStrategyId}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4ECDC4',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                {loading ? 'Launching...' : 'YES'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Attack Other Country Dialog */}
      {attackOtherCountryDialog.show && (
        <div className="attack-dialog-overlay" style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          backgroundColor: 'rgba(0, 0, 0, 0.8)', 
          zIndex: 4000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div className="attack-dialog" style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.95)', 
            border: '2px solid rgba(255, 255, 255, 0.3)', 
            borderRadius: '12px', 
            padding: '30px', 
            maxWidth: '500px', 
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#FFD700' }}>
              Attack Other Country
            </h3>
            <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.6' }}>
              This country ({attackOtherCountryDialog.countryName}) is controlled by <strong style={{ color: '#FF6B6B' }}>{attackOtherCountryDialog.ownerFaction}</strong>.
              If you win, you will take control of this country.
            </p>
            
            {/* Strategy Selection */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', color: '#CCCCCC' }}>
                Select Strategy:
              </label>
              {userStrategies.length === 0 ? (
                <div style={{ color: '#ff4444', fontSize: '14px', marginBottom: '10px' }}>
                  No strategies available for this purpose
                </div>
              ) : (
                <CustomDropdown
                  options={userStrategies.map(s => ({ value: s.id, label: s.name }))}
                  value={attackOtherCountryDialog.selectedStrategyId}
                  onChange={(value) => setAttackOtherCountryDialog(prev => ({ ...prev, selectedStrategyId: value }))}
                  placeholder="Select a strategy"
                />
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAttackOtherCountryDialog({ show: false, countryCellId: null, countryName: null, ownerFaction: null, selectedStrategyId: null })}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAttackOtherCountry}
                disabled={loading || !attackOtherCountryDialog.selectedStrategyId}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4ECDC4',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                {loading ? 'Launching...' : 'Attack'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WarEffortFortification;
